/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: FeatureEstimateFields.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-FEATURES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * FeatureEstimateFields - Поля оценок по ролям в карточной сетке.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: карточки ролей с цветным чипом, счётчик суммарных часов.
 * LAST_CHANGE: 2026-07-13 - Anti-autocomplete для полей оценок через mergeAppDataFieldSlotProps.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import { getColorByName } from '../../../utils/projectColors';
import { mergeAppDataFieldSlotProps } from '../../../utils/formFieldConfig';

/* START_METHOD_isEstimateInputValid */
/* START_CONTRACT: isEstimateInputValid
 * PURPOSE: Проверить корректность введённой оценки (пусто или число 0..10000).
 * INPUTS: { value: string|null }.
 * OUTPUTS: { boolean }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-FEATURES
 * END_CONTRACT: isEstimateInputValid
 */
export function isEstimateInputValid(value) {
  if (value === '' || value == null) {
    return true;
  }
  const num = parseFloat(value);
  return !Number.isNaN(num) && num >= 0 && num <= 10000;
}
/* END_METHOD_isEstimateInputValid */

/* START_METHOD_buildEstimatesPayload */
/* START_CONTRACT: buildEstimatesPayload
 * PURPOSE: Собрать payload оценок по ролям, отбросив пустые и некорректные значения.
 * INPUTS: { estimatesByRoleId: object, roles: ProjectRole[] }.
 * OUTPUTS: { { projectRoleId: number, estimate: number }[] }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-FEATURES
 * END_CONTRACT: buildEstimatesPayload
 */
export function buildEstimatesPayload(estimatesByRoleId, roles) {
  return roles
    .map((role) => {
      const raw = estimatesByRoleId[role.id];
      if (raw === '' || raw == null) {
        return null;
      }
      const estimate = parseFloat(raw);
      if (Number.isNaN(estimate)) {
        return null;
      }
      return { projectRoleId: role.id, estimate };
    })
    .filter(Boolean);
}
/* END_METHOD_buildEstimatesPayload */

/* START_METHOD_estimatesToRoleMap */
/* START_CONTRACT: estimatesToRoleMap
 * PURPOSE: Преобразовать массив оценок в карту roleId -> строковое значение для формы.
 * INPUTS: { estimates: object[]|null }.
 * OUTPUTS: { Record<number, string> }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-FEATURES
 * END_CONTRACT: estimatesToRoleMap
 */
export function estimatesToRoleMap(estimates) {
  return Object.fromEntries(
    (estimates ?? []).map((item) => [item.projectRoleId, String(item.estimate)])
  );
}
/* END_METHOD_estimatesToRoleMap */

function sumValidEstimates(estimatesByRoleId) {
  return Object.values(estimatesByRoleId).reduce((sum, raw) => {
    if (raw === '' || raw == null) return sum;
    const num = parseFloat(raw);
    if (Number.isNaN(num)) return sum;
    return sum + num;
  }, 0);
}

/* START_METHOD_FeatureEstimateFields */
/* START_CONTRACT: FeatureEstimateFields
 * PURPOSE: Поля ввода оценок задачи по проектным ролям с валидацией.
 * INPUTS: { roles: ProjectRole[], estimatesByRoleId: object, onEstimateChange: (roleId,value)=>void, disabled: boolean }.
 * OUTPUTS: { JSX.Element - поля оценок или подсказка }.
 * SIDE_EFFECTS: нет (делегирует onEstimateChange).
 * LINKS: M-FE-FEATURES
 * END_CONTRACT: FeatureEstimateFields
 */
export default function FeatureEstimateFields({
  roles,
  estimatesByRoleId,
  onEstimateChange,
  disabled,
}) {
  if (!roles.length) {
    return (
      <Paper
        variant="outlined"
        sx={{
          py: 2.5,
          px: 2,
          textAlign: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Сначала добавьте проектные роли — без них оценки не назначить.
        </Typography>
      </Paper>
    );
  }

  const totalHours = sumValidEstimates(estimatesByRoleId);

  return (
    <Box>
      {totalHours > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 1.5,
            color: 'text.secondary',
            fontFamily: '"Source Sans 3", monospace',
            fontWeight: 600,
          }}
        >
          Итого: {totalHours} ч
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {roles.map((role) => {
          const value = estimatesByRoleId[role.id] ?? '';
          const invalid = !isEstimateInputValid(value);
          const roleColor = getColorByName(role.name);

          return (
            <Paper
              key={role.id}
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                borderColor: invalid ? 'error.main' : alpha(roleColor, 0.2),
                bgcolor: alpha(roleColor, 0.03),
              }}
            >
              <Chip
                label={role.shortName}
                size="small"
                sx={{
                  mt: 0.75,
                  height: 22,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  bgcolor: roleColor,
                  color: '#fff',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {role.name}
                </Typography>
                <TextField
                  fullWidth
                  label={`Часы · ${role.shortName}`}
                  name={`teamplanner-feature-estimate-${role.id}`}
                  type="number"
                  value={value}
                  onChange={(e) => onEstimateChange(role.id, e.target.value)}
                  size="small"
                  disabled={disabled}
                  error={invalid}
                  helperText={invalid ? 'От 0 до 10 000' : ' '}
                  slotProps={mergeAppDataFieldSlotProps({
                    htmlInput: { min: 0, max: 10000, step: 'any', inputMode: 'decimal' },
                  })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    },
                  }}
                />
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
/* END_METHOD_FeatureEstimateFields */
